import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import RbacService, { AdminUser } from "@/app/_services/RbacService";
import { inviteUserSchema } from "./InviteUserSchema";
import { toast } from "@/app/_utils/toast";

type FormData = yup.InferType<typeof inviteUserSchema>;

const EMPTY_VALUES: FormData = {
  email: "",
  first_name: "",
  last_name: "",
  role_ids: [],
};

export function useInviteUserFlow({
  editingUser,
  onSaved,
}: {
  editingUser?: AdminUser | null;
  onSaved: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(inviteUserSchema),
    values: editingUser
      ? {
          email: editingUser.email,
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          role_ids: editingUser.roles.map((role) => role.id),
        }
      : EMPTY_VALUES,
  });

  const handleSave = handleSubmit(async (fields: FieldValues) => {
    setIsSaving(true);
    try {
      if (editingUser) {
        // Email and name belong to WorkOS once the invite is accepted, so editing an
        // existing user only changes roles.
        await RbacService.updateUserRolesAsync(
          editingUser.id,
          fields.role_ids as number[]
        );
        toast.success("Roles updated");
      } else {
        await RbacService.inviteUserAsync({
          email: String(fields.email).trim().toLowerCase(),
          first_name: String(fields.first_name ?? "").trim(),
          last_name: String(fields.last_name ?? "").trim(),
          role_ids: fields.role_ids as number[],
        });
        toast.success(`Invitation sent to ${fields.email}`);
        reset(EMPTY_VALUES);
      }
      onSaved();
    } catch (e: any) {
      toast.error(e?.message || "Could not save the user");
    } finally {
      setIsSaving(false);
    }
  });

  return { control, errors, isSaving, handleSave };
}
